import React, { useState, useEffect, useRef } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import quizService from '../../services/quizService';
import toastService from '../../services/toastService';

const CodeQuestion = ({ question, answer, onAnswerChange, inputRef, onLanguageChange }) => {
  console.log('🔧 CodeQuestion RENDER:', question.id, 'onLanguageChange:', !!onLanguageChange);
  
  const [showPreview, setShowPreview] = useState(false);
  const [executionResult, setExecutionResult] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [showOutput, setShowOutput] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [stdinInput, setStdinInput] = useState('');
  const [executionCount, setExecutionCount] = useState(0);
  const [lastExecutionTime, setLastExecutionTime] = useState(0);
  
  const executionTimeoutRef = useRef(null);

  // Load supported languages when component mounts
  useEffect(() => {
    console.log('🔧 CodeQuestion MOUNTED:', question.id);
    loadLanguages();
  }, []);

  // Track when selectedLanguage changes
  useEffect(() => {
    console.log('🔧 selectedLanguage changed:', selectedLanguage, 'for question:', question.id);
  }, [selectedLanguage]);

  const loadLanguages = async () => {
    try {
      const result = await quizService.getCompilerLanguages();
      
      if (result.success && result.data) {
        setAvailableLanguages(result.data);
        
        // Set default language
        if (result.data.length > 0) {
          // If question has predefined language, use it, otherwise use first in list
          const defaultLang = question.language || result.data[0].value;
          setSelectedLanguage(defaultLang);
          
          // Trigger onLanguageChange callback if available
          if (onLanguageChange) {
            const langObj = result.data.find(l => l.value === defaultLang);
            // Send the language value (not name) to match API expectations
            onLanguageChange(question.id, langObj?.value || defaultLang);
          }
        }
      } else {
        toastService.error(result.message || 'Failed to load supported languages');
      }
    } catch (error) {
      console.error('Load languages error:', error);
      toastService.error('Failed to load supported languages');
    }
  };

  const handleChange = (value) => {
    // Check code size limit (prevent abuse)
    if (value.length > 10000) { // 10KB limit
      toastService.error('Code size too large. Maximum 10KB allowed.');
      return;
    }
    onAnswerChange(question.id, value);
  };

  const handleLanguageChange = (language) => {
    console.log('🔧 CodeQuestion handleLanguageChange:', { questionId: question.id, language });
    setSelectedLanguage(language);
    // Notify parent of language change
    if (onLanguageChange) {
      console.log('🔧 Calling parent onLanguageChange');
      const langObj = availableLanguages.find(l => l.value === language);
      onLanguageChange(question.id, langObj?.value || language);
    } else {
      console.log('❌ onLanguageChange prop is missing');
    }
    // Clear output when language changes
    setExecutionResult(null);
    setShowOutput(false);
  };

  const resetCode = () => {
    onAnswerChange(question.id, '');
    setExecutionResult(null);
    setShowOutput(false);
    setStdinInput('');
  };

  const runCode = async () => {
    // Security checks
    const now = Date.now();
    
    // Prevent spamming (max 3 executions per 30 seconds)
    if (executionCount >= 3 && (now - lastExecutionTime) < 30000) {
      toastService.warning('Too many executions. Please wait before running again.');
      return;
    }

    if (!answer || !answer.trim()) {
      toastService.error('Please enter some code to run.');
      return;
    }

    // Auto-select first language if none selected
    if (!selectedLanguage && availableLanguages.length > 0) {
      const firstLanguage = availableLanguages[0].value;
      setSelectedLanguage(firstLanguage);
      if (onLanguageChange) {
        const langObj = availableLanguages.find(l => l.value === firstLanguage);
        onLanguageChange(question.id, langObj?.value || firstLanguage);
      }
    }

    // if (!selectedLanguage) {
    //   toastService.error('Please select a programming language.');
    //   return;
    // }

    setIsRunning(true);
    setShowOutput(true);
    setExecutionResult(null);

    try {
      const result = await quizService.executeCode(answer, selectedLanguage, stdinInput);
      
      if (result.success) {
        setExecutionResult(result.data);
        
        // Update execution tracking
        setExecutionCount(prev => prev + 1);
        setLastExecutionTime(now);
        
        // Reset execution count after 30 seconds
        if (executionTimeoutRef.current) {
          clearTimeout(executionTimeoutRef.current);
        }
        executionTimeoutRef.current = setTimeout(() => {
          setExecutionCount(0);
        }, 30000);
        
      } else {
        setExecutionResult({
          stdout: '',
          stderr: result.message || 'Execution failed'
        });
        toastService.error(result.message || 'Code execution failed');
      }
    } catch (error) {
      console.error('Run code error:', error);
      setExecutionResult({
        stdout: '',
        stderr: 'Network error. Please check your connection.'
      });
      toastService.error('Network error. Please check your connection.');
    } finally {
      setIsRunning(false);
    }
  };

  const getLanguageDisplayName = () => {
    const lang = availableLanguages.find(l => l.value === selectedLanguage);
    return lang ? lang.name : selectedLanguage;
  };

  return (
    <div className="code-question">
      <div className="code-header">
        <div className="question-header">
          <div className="question-instructions">Write your code and run it to test</div>
        </div>
        <div className="language-selector">
          <label htmlFor="language-select">Language:</label>
          <select
            id="language-select"
            value={selectedLanguage}
            onChange={(e) => handleLanguageChange(e.target.value)}
            disabled={isRunning}
          >
            {availableLanguages.map((lang) => (
              <option key={lang.value} value={lang.value}>
                {lang.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="code-editor">
        <textarea
          ref={inputRef}
          value={answer || ''}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={`Write your ${getLanguageDisplayName()} code here...`}
          className="code-textarea"
          data-allow-editor-clipboard="true"
          spellCheck={false}
          disabled={isRunning}
        />
      </div>

      {/* <div className="stdin-section">
        <label htmlFor="stdin-input">Input (stdin):</label>
        <textarea
          id="stdin-input"
          value={stdinInput}
          onChange={(e) => setStdinInput(e.target.value)}
          placeholder="Optional input for your program..."
          className="stdin-textarea"
          rows={3}
          disabled={isRunning}
        />
      </div> */}

      <div className="code-controls">
        <button
          onClick={() => setShowPreview(!showPreview)}
          className="preview-btn"
          disabled={isRunning}
        >
          {showPreview ? 'Hide Preview' : 'Show Preview'}
        </button>
        <button
          onClick={runCode}
          disabled={isRunning || !answer || !selectedLanguage}
          className="run-btn"
        >
          {isRunning ? 'Running...' : 'Run Code'}
        </button>
        <button
          onClick={resetCode}
          className="reset-btn"
          disabled={isRunning}
        >
          Reset Code
        </button>
      </div>

      {showPreview && (
        <div className="code-preview">
          <h4>Code Preview:</h4>
          <SyntaxHighlighter
            language={selectedLanguage}
            style={vscDarkPlus}
            customStyle={{
              borderRadius: '8px',
              fontSize: '14px',
            }}
          >
            {answer || '// No code written yet'}
          </SyntaxHighlighter>
        </div>
      )}

      {showOutput && executionResult && (
        <div className="execution-output">
          <h4>Output:</h4>
          {executionResult.stdout && (
            <div className="stdout-section">
              <h5>Standard Output:</h5>
              <pre className="stdout-content">{executionResult.stdout}</pre>
            </div>
          )}
          {executionResult.stderr && (
            <div className="stderr-section">
              <h5 className="error-title">Error Output:</h5>
              <pre className="stderr-content error">{executionResult.stderr}</pre>
            </div>
          )}
          {!executionResult.stdout && !executionResult.stderr && (
            <div className="no-output">
              <em>No output</em>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CodeQuestion;
